import { customElement } from 'lit/decorators.js'
import { html, LitElement, nothing, type CSSResultGroup } from 'lit'
import type { DeliveryDate, DropoffMethod, OpeningHours } from './api/shipment-methods.js'
import { hostStyle } from './delivery-match-method.style.js'
import { consume } from '@lit/context'
import { style } from './delivery-match-method-dropoff.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { DeliveryMatchActionsContext, DeliveryMatchCurrencyContext, DeliveryMatchSelectedMethodContext, type DeliveryMatchActionsContextType, type DeliveryMatchCurrencyContextType, type DeliveryMatchSelectedMethodContextType } from './provider.js'
import { getCarrierImage, getRelativeDateInfo, getRelativeDistanceInfo, priceFormat } from './utils.js'
import { GoogleMapsMapProviderActionsContext, type GoogleMapsMapProviderActionsContextType } from './map-provider.js'

@customElement('csc-delivery-match-method-dropoff')
export class CSCDeliveryMatchMethodDropoff extends LitElement {
  method: DropoffMethod
  showRadioButton?: boolean

  static override styles?: CSSResultGroup = [hostStyle, style]

  @consume({ context: DeliveryMatchActionsContext })
  private actions!: DeliveryMatchActionsContextType

  @consume({ context: DeliveryMatchSelectedMethodContext, subscribe: true })
  selectedMethod!: DeliveryMatchSelectedMethodContextType

  @consume({ context: GoogleMapsMapProviderActionsContext })
  private mapActions!: GoogleMapsMapProviderActionsContextType

  handleChangeMethod = () => {
    this.mapActions.setCenter({
      lat: this.method.address.latitude,
      lon: this.method.address.longitude
    })
    this.mapActions.setZoomLevel(16)
    this.actions.setSelectedMethod(this.method.methodId)
  }

  private renderRadioButton = () => {
    if (!this.showRadioButton) return nothing
    return html`
      <cse-radio-button
        id="delivery-match-method-${this.method.methodId}"
        value=${this.method.methodId}
        ?checked=${this.selectedMethod === this.method.methodId}
      ></cse-radio-button>
    `
  }

  constructor({ method, showRadioButton }: {
    method: DropoffMethod,
    showRadioButton?: boolean,
  }) {
    super()
    this.method = method
    this.showRadioButton = showRadioButton ?? false
  }

  override render() {
    const isSelected = this.selectedMethod === this.method.methodId

    const methodDropoffContent = new CSCDeliveryMatchMethodDropoffContent({ selectedMethod: this.method })

    return html`<cse-selectable
        size="small"
        state="${isSelected ? 'selected' : 'selectable'}"
        @click="${this.handleChangeMethod}"
      >
      <div class="content">
        ${this.renderRadioButton()}
        ${methodDropoffContent}
      </div>
  </cse-selectable>`
  }
}

@customElement('csc-delivery-match-method-dropoff-content')
export class CSCDeliveryMatchMethodDropoffContent extends LitElement {
  selectedMethod: DropoffMethod

  constructor({ selectedMethod }: { selectedMethod: DropoffMethod }) {
    super()
    this.selectedMethod = selectedMethod
  }

  static override styles?: CSSResultGroup = [hostStyle, style]

  @consume({ context: DeliveryMatchCurrencyContext })
  currencyContext!: DeliveryMatchCurrencyContextType

  renderPrice = (method: DropoffMethod) => {
    const priceAsNumber = parseFloat(method.price)

    if (isNaN(priceAsNumber) || priceAsNumber === 0) {
      return html`<span class="method-price-wrapper"><cse-text class="method-price method-price-free">
        ${t('free_delivery', { ns: 'csc-delivery-match', default: 'Free delivery' })}
      </cse-text></span>`
    }

    const formattedPrice = priceFormat(this.currencyContext.locale, method.currency, method.price)

    return html`<span class="method-price-wrapper"><cse-text class="method-price">${formattedPrice}</cse-text></span>`
  }

  renderCarrier = (method: DropoffMethod) => {
    // TODO: Find a way to show the carrier images.
    // For now, testing assets it is...
    return html`<cse-image
      class="carrier-image"
      source="${getCarrierImage(method.carrier.code)}"
      fallback-source="${getCarrierImage('fallback')}"
      alt="${method.carrier.name}"
      height="${40}"
      fit="contain"
    />`
  }

  private getDayFromIndex(index: number) {
    const dayMap = [
      t('day_monday', { ns: 'csc-delivery-match', default: 'Monday' }),
      t('day_tuesday', { ns: 'csc-delivery-match', default: 'Tuesday' }),
      t('day_wednesday', { ns: 'csc-delivery-match', default: 'Wednesday' }),
      t('day_thursday', { ns: 'csc-delivery-match', default: 'Thursday' }),
      t('day_friday', { ns: 'csc-delivery-match', default: 'Friday' }),
      t('day_saturday', { ns: 'csc-delivery-match', default: 'Saturday' }),
      t('day_sunday', { ns: 'csc-delivery-match', default: 'Sunday' })
    ]

    return dayMap[index] ?? 'NO CORRECT INDEX'
  }

  private renderDeliveryMoment = (deliveryDate: DeliveryDate) => {
    if (!deliveryDate.date) return nothing
    // Logic
    /*
      When the diffInDays < 2 (today or tomorrow)
      -> Show (Today | Tomorrow) + (Weekday)
      Otherwise
      -> Show (Weekday + Day + Month)
    */

    const deliveryDateDate = deliveryDate.date
    const { dateFull } = getRelativeDateInfo(deliveryDateDate, new Date(), this.currencyContext.locale)

    const text = t('close_delivery', { default: 'Pickup {{variable_day}}', ns: 'csc-delivery-match', variable_day: dateFull })
    return html`
      <cse-text class="method-description">
        ${text}
      </cse-text>
    `
  }

  private openingTimesToObject(openingHours: OpeningHours[]): Record<number, OpeningHours> {
    return openingHours.reduce((prev, curr) => {
      return {
        ...prev,
        [curr.day]: curr
      }
    }, {})
  }

  private handleAccordionClick = (event: Event) => {
    event.stopPropagation()
  }

  private renderOpeningTimes(method: DropoffMethod) {
    if (method.openingHours.length <= 0) {
      return nothing
    }

    const renderTimes = (hours: OpeningHours) => {
      if (hours.openFrom === '00:00' && hours.openTo === '00:00') {
        return html`${t('opening_hours_closed', { ns: 'csc-delivery-match', default: 'Closed' })}`
      }
      if (hours.openFrom === '00:01' && hours.openTo === '23:59') {
        return html`${t('opening_hours_all_day', { ns: 'csc-delivery-match', default: 'Open all day' })}`
      }
      return `${hours.openFrom} - ${hours.openTo}`
    }

    const openingHoursAsObject = this.openingTimesToObject(method.openingHours)

    return html`
        <cse-accordion
          variant="simple"
          @click="${this.handleAccordionClick}"
        >
          <div slot="title">
            <cse-link>
              ${t('dropoff_method_opening_times', { ns: 'csc-delivery-match', default: 'Opening times' })}
            </cse-link>
          </div>
            <div class="opening-hours-grid" slot="content">
              ${Object.entries(openingHoursAsObject).map(([, day]) => html`
                  <div>${this.getDayFromIndex(day.day)}</div>
                  <div>${renderTimes(day)}</div>
                `
              )}
          </div>
        </cse-accordion>`
  }

  private renderDescription(method: DropoffMethod) {
    if (!method.deliveryDate.date) {
      return nothing
    }

    return html`
      ${this.renderDeliveryMoment(method.deliveryDate)}
    `
  }

  override render() {
    const method = this.selectedMethod
    if (!method) return nothing

    return html`
    <div class="method-heading">
      <cse-text variant="heading-5" class="method-name">${method.name}</cse-text>
      ${this.renderPrice(method)}
    </div>
    <cse-text variant="caption-l" class="method-distance">
      <cse-icon icon-role="csc-delivery-match.dropoff-method-distance"></cse-icon>
      ${getRelativeDistanceInfo(method.distance)}
    </cse-text>
    <div class="method-address-wrapper">
      <cse-text class="method-address">
        ${method.address.street} ${method.address.number}<br/>
        ${method.address.zipcode} ${method.address.city}
      </cse-text>
      ${this.renderCarrier(method)}
    </div>
    <div class="method-bottom">
      <div>
        ${this.renderDescription(method)}
        ${this.renderOpeningTimes(method)}
      </div>
    </div>`
  }
}
